import { Points, Vector2, Vector3 } from 'three';

import { isDefined } from '../../../../../../utils/variables';
import cameraManager from '../../../../../camera-manager';
import { DesignNode, RevertChange } from '../../../../../elements/design/design-node/design-node';
import {
  getBulgeFromPoints,
  getSweep,
  maxBulge,
} from '../../../../../elements/design/design-node/utils/arc';
import {
  Conic,
  getArcConics,
  getConicDirection,
  getConicLength,
  getConicParameter,
  getConicParametersAtLengths,
  getConicPart,
  getConicPoint,
  getConicSegmentCount,
  getSegmentConic,
  getSegmentCurve,
  SegmentCurve,
  tessellateConicAsLineSegments,
} from '../../../../../elements/design/design-node/utils/conic';
import { LineSegmentId } from '../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../elements/design/design-node/utils/point-node';
import { AttributeData, AttributeId } from '../../../../../elements/design/skymap-extras';
import { Stripe } from '../../../../../elements/styled-lines/styled-segment';
import { findPointOnLineFromHeight } from '../../../../../utilities/math-helpers';
import { DesignTool } from '../../../../design-tool';

/**
 * The number of straight steps that curves are shown with in cursors and previews.
 */
const cursorSteps = 64;

/**
 * How far (meters) the straight line segments may be from the curve when replacing a curve with them.
 */
const densifyTolerance = 0.005;

/**
 * Limits the number of straight line segments a curve is replaced with, for very large curves.
 */
const maxDensifySegments = 256;

/**
 * Gets the vertices for showing curves in a cursor, as pairs of points along them.
 */
export function getConicCursorVertices(conics: Conic[]): Vector3[] {
  return conics.flatMap((conic) =>
    tessellateConicAsLineSegments(conic, Math.ceil(cursorSteps / conics.length)),
  );
}

/**
 * Gets the vertices for showing a line in a cursor, as pairs of points. Curves are split into steps.
 */
export function getLineCursorVertices(
  start: Vector3,
  end: Vector3,
  curve: SegmentCurve | undefined,
): Vector3[] {
  return curve
    ? getConicCursorVertices([getSegmentConic(start, end, curve)])
    : [start.clone(), end.clone()];
}

/**
 * Gets the vertices for showing a line segment in a cursor, in world coordinates.
 */
export function getLineSegmentCursorVertices(node: DesignNode, id: LineSegmentId): Vector3[] {
  const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
  return getLineCursorVertices(start, end, node.getLineSegmentById(id).curve);
}

/**
 * Gets the line segment from start to end, curved if it has a curve.
 */
export function getLineSegmentConic(node: DesignNode, id: LineSegmentId, mode: 'local' | 'world') {
  const [start, end] = node.getVerticesByLineSegmentId(id, mode);
  const curve = node.getLineSegmentById(id).curve;
  return curve && getSegmentConic(start, end, curve);
}

/**
 * Gets the world position of the middle of the line segment, along the curve if it is curved.
 */
export function getLineSegmentMiddle(node: DesignNode, id: LineSegmentId): Vector3 {
  const conic = getLineSegmentConic(node, id, 'world');
  const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
  return conic ? getConicPoint(conic, 0.5) : start.add(end).multiplyScalar(0.5);
}

/**
 * Gets the curves of the circular arc from start to end that passes through the point, in the
 * horizontal plane, in pieces that are small enough for one curve each. Returns no curves if the
 * point is on the line through start and end.
 */
export function getArcThroughPoint(start: Vector3, through: Vector3, end: Vector3): Conic[] {
  return getArcCurves(start, end, getBulgeFromPoints(start, through, end));
}

/**
 * Gets the curves of the circular arc with the bulge, see getArcThroughPoint.
 */
export function getArcCurves(start: Vector3, end: Vector3, bulge: number): Conic[] {
  return bulge === 0 ? [] : getArcConics(start, end, bulge);
}

/**
 * A circular arc from start to end, see getArcCurves.
 */
export type Arc = { start: Vector3; end: Vector3; bulge: number };

/**
 * The shape of a curve between its ends: a circular arc with the bulge, or one that leaves the start
 * in the direction (in the horizontal plane), or arrives at the end in it. In both directions, it is
 * two arcs that meet smoothly, a biarc, since one arc seldom can.
 */
export type CurveShape = { bulge: number } | { startDirection?: Vector2; endDirection?: Vector2 };

/**
 * Gets the arcs of the curve from start to end with the shape, in the same coordinates.
 */
export function getCurveArcs(start: Vector3, end: Vector3, shape: CurveShape): Arc[] {
  if ('bulge' in shape) {
    return [{ start, end, bulge: shape.bulge }];
  }

  const { startDirection, endDirection } = shape;

  if (startDirection && endDirection) {
    return getBiarc(start, startDirection, end, endDirection);
  }

  if (startDirection) {
    return [{ start, end, bulge: getBulgeFromDirection(start, end, startDirection) }];
  }

  // The arc back from the end, leaving it the opposite way, is the same arc reversed
  return endDirection
    ? [{ start, end, bulge: -getBulgeFromDirection(end, start, endDirection.clone().negate()) }]
    : [{ start, end, bulge: 0 }];
}

/**
 * Gets the shape of the same curve, from its end to its start.
 */
export function reverseCurveShape(shape: CurveShape): CurveShape {
  return 'bulge' in shape
    ? { bulge: -shape.bulge }
    : {
        startDirection: shape.endDirection?.clone().negate(),
        endDirection: shape.startDirection?.clone().negate(),
      };
}

/**
 * Gets the bulge of the arc from start to end that leaves the start in the direction.
 */
function getBulgeFromDirection(start: Vector3, end: Vector3, direction: Vector2): number {
  const chord = new Vector2(end.x - start.x, end.y - start.y);

  if (chord.lengthSq() === 0) {
    return 0;
  }

  // The arc leaves at half its sweep from the chord, to the right of it for a positive bulge, which
  // sweeps counterclockwise
  const angle = Math.atan2(chord.cross(direction), chord.dot(direction));
  const bulge = Math.tan(-angle / 2);
  return Math.max(-maxBulge, Math.min(bulge, maxBulge));
}

/**
 * Gets the two arcs from start to end that leave the start in its direction and arrive at the end in
 * its, meeting where they have the same direction. They are the arcs whose tangents at the ends,
 * from the ends to where they meet, are equally long, which gives two arcs alike when the curve is
 * symmetric. The height changes evenly along both.
 */
function getBiarc(
  start: Vector3,
  startDirection: Vector2,
  end: Vector3,
  endDirection: Vector2,
): Arc[] {
  const chord = new Vector2(end.x - start.x, end.y - start.y);
  const sum = startDirection.clone().add(endDirection);
  // 0 when the directions are the same
  const spread = 2 * (1 - startDirection.dot(endDirection));
  const chordSum = chord.dot(sum);

  // The length d of the tangents, from |chord - d * sum| = 2d
  const length =
    spread < 1e-9
      ? chordSum === 0
        ? undefined
        : chord.lengthSq() / (2 * chordSum)
      : (-chordSum + Math.sqrt(chordSum * chordSum + spread * chord.lengthSq())) / spread;

  // Going the same way, straight across: two half circles that meet in the middle
  const joint2d = isDefined(length)
    ? new Vector2(start.x, start.y)
        .addScaledVector(startDirection, length)
        .add(new Vector2(end.x, end.y).addScaledVector(endDirection, -length))
        .multiplyScalar(0.5)
    : new Vector2((start.x + end.x) / 2, (start.y + end.y) / 2);
  const joint = new Vector3(joint2d.x, joint2d.y, (start.z + end.z) / 2);

  const first = getBulgeFromDirection(start, joint, startDirection);
  const second = -getBulgeFromDirection(end, joint, endDirection.clone().negate());

  const firstLength = getArcLength(start, joint, first);
  const total = firstLength + getArcLength(joint, end, second);
  joint.z = total > 0 ? start.z + ((end.z - start.z) * firstLength) / total : joint.z;

  const arcs = [
    { start, end: joint, bulge: first },
    { start: joint, end, bulge: second },
  ].filter((arc) => Math.hypot(arc.end.x - arc.start.x, arc.end.y - arc.start.y) > 1e-9);

  // Lines that go straight on are joined by one straight line, without a point between two halves
  return arcs.every((arc) => Math.abs(arc.bulge) < 1e-9) ? [{ start, end, bulge: 0 }] : arcs;
}

/**
 * Gets the length of the arc in the horizontal plane.
 */
function getArcLength(start: Vector3, end: Vector3, bulge: number): number {
  const chordLength = Math.hypot(end.x - start.x, end.y - start.y);
  const halfSweep = getSweep(bulge) / 2;
  return halfSweep === 0 ? chordLength : (chordLength * halfSweep) / Math.sin(halfSweep);
}

/**
 * Where a curve ends: at a point of the design, on a line of it, or neither.
 */
export type CurveEndAt = { position: Vector3; pointId?: PointId; lineSegmentId?: LineSegmentId };

/**
 * Gets the shape of the curve from start to end that follows the lines at its ends, see
 * getLineDirections, at each end the line closest to the direction of the arc with the bulge there.
 * Undefined if there are no lines at either end. In world coordinates.
 */
export function getAlignedShape(
  node: DesignNode,
  start: CurveEndAt,
  end: CurveEndAt,
  bulge: number,
): CurveShape | undefined {
  const [leaving, arriving] = getArcDirections(start.position, end.position, bulge);
  const startDirection = getClosestDirection(getLineDirections(node, start, end.pointId), leaving);
  // Into the line at the end, the opposite way out of it
  const endDirection = getClosestDirection(
    getLineDirections(node, end, start.pointId).map((x) => x.negate()),
    arriving,
  );

  return startDirection || endDirection ? { startDirection, endDirection } : undefined;
}

/**
 * Gets the shape in the design's own coordinates, whose directions may be turned from the world's.
 */
export function getLocalShape(node: DesignNode, shape: CurveShape): CurveShape {
  if ('bulge' in shape) {
    return shape;
  }

  const origin = node.getLocalPosition(new Vector3());
  const toLocal = (direction: Vector2 | undefined) => {
    if (!direction) {
      return undefined;
    }
    const local = node.getLocalPosition(new Vector3(direction.x, direction.y, 0)).sub(origin);
    return new Vector2(local.x, local.y).normalize();
  };

  return {
    startDirection: toLocal(shape.startDirection),
    endDirection: toLocal(shape.endDirection),
  };
}

/**
 * Adds line segments along the arcs, in local coordinates, from one point to the other, with points
 * where the arcs meet, and where an arc too large for one curve is split. The stripes continue from
 * one to the next. Returns the changes.
 */
export function addArcLineSegments(
  node: DesignNode,
  from: PointId,
  to: PointId,
  arcs: Arc[],
  attributes: Map<AttributeId, AttributeData>,
  style?: { width?: number; stripe?: Stripe },
): RevertChange[] {
  const changes: RevertChange[] = [];
  let previous = from;
  let offset = 0;

  arcs.forEach((arc, i) => {
    let next = to;

    if (i < arcs.length - 1) {
      const change = node.addPoint(arc.end.clone());
      changes.unshift(change);
      next = change.id;
    }

    const conics = getArcCurves(arc.start, arc.end, arc.bulge);
    changes.unshift(
      ...node.addCurvedLineSegments(previous, next, conics, attributes, {
        width: style?.width,
        stripe: style?.stripe && { ...style.stripe, offset: style.stripe.offset + offset },
      }),
    );

    offset +=
      conics.length > 0
        ? conics.reduce((length, conic) => length + getConicLength(conic), 0)
        : arc.start.distanceTo(arc.end);
    previous = next;
  });

  return changes;
}

/**
 * Gets the directions (in the horizontal plane) that a curve at the end would continue the lines
 * there in: out of the end of each line that ends at the point, other than one to the other point,
 * or both ways along the line that the end is on.
 */
export function getLineDirections(
  node: DesignNode,
  at: { position: Vector3; pointId?: PointId; lineSegmentId?: LineSegmentId },
  otherPointId?: PointId,
): Vector2[] {
  const directionAt = (id: LineSegmentId, position: Vector3) => {
    const conic = getLineSegmentConic(node, id, 'world');
    const [a, b] = node.getVerticesByLineSegmentId(id, 'world');
    return conic
      ? getConicDirection(conic, getConicParameter(conic, position))
      : new Vector2(b.x - a.x, b.y - a.y);
  };

  const directions: Vector2[] = [];

  if (isDefined(at.pointId)) {
    const position = node.getVertexByPointId(at.pointId, 'world');

    for (const id of node.getPointById(at.pointId).segments) {
      const { values } = node.getLineSegmentById(id);

      if (!isDefined(otherPointId) || !values.includes(otherPointId)) {
        const direction = directionAt(id, position);
        directions.push(values[1] === at.pointId ? direction : direction.negate());
      }
    }
  } else if (isDefined(at.lineSegmentId)) {
    const direction = directionAt(at.lineSegmentId, at.position);
    directions.push(direction, direction.clone().negate());
  }

  // A vertical line has no direction in the horizontal plane
  return directions.filter((x) => x.lengthSq() > 0).map((x) => x.normalize());
}

/**
 * Gets the direction the arc leaves its start in and the one it arrives at its end in, in the
 * horizontal plane.
 */
export function getArcDirections(start: Vector3, end: Vector3, bulge: number): [Vector2, Vector2] {
  const chord = new Vector2(end.x - start.x, end.y - start.y).normalize();
  // At half the sweep from the chord, to the right of it at the start for a positive bulge
  const halfSweep = getSweep(bulge) / 2;
  return [
    chord.clone().rotateAround(new Vector2(), -halfSweep),
    chord.rotateAround(new Vector2(), halfSweep),
  ];
}

/**
 * Gets the direction closest to the given one, if there are any.
 */
export function getClosestDirection(directions: Vector2[], to: Vector2): Vector2 | undefined {
  return directions.reduce<Vector2 | undefined>(
    (closest, x) => (!closest || x.dot(to) > closest.dot(to) ? x : closest),
    undefined,
  );
}

/**
 * How the size of a curve is typed: its radius, or its bulge, how far its middle pokes out from the
 * line between its ends.
 */
export type CurveMeasure = 'radius' | 'bulge';

/**
 * Gets the bulge of the arc from start to end that passes through the point. With a typed distance,
 * the arc has that radius or bulge instead, see CurveMeasure, on the side of the point, which is the
 * right side if the point is on the line. A radius is the shorter of the two arcs with it, unless
 * the point is on the longer one.
 */
export function getArcBulge(
  start: Vector3,
  end: Vector3,
  through: Vector3 | undefined,
  typed?: { measure: CurveMeasure; distance: number },
): number {
  const bulge = through ? getBulgeFromPoints(start, through, end) : 0;

  if (!isDefined(typed)) {
    return bulge;
  }

  const chordLength = Math.hypot(end.x - start.x, end.y - start.y);

  if (chordLength === 0) {
    return 0;
  }

  const magnitude = Math.min(
    typed.measure === 'radius'
      ? getBulgeFromRadius(chordLength, typed.distance, Math.abs(bulge) > 1)
      : // The middle of the arc is bulge * chord / 2 from the chord.
        (2 * Math.abs(typed.distance)) / chordLength,
    maxBulge,
  );
  return bulge < 0 ? -magnitude : magnitude;
}

/**
 * Whether an arc from start to end can have the typed radius or bulge, see CurveMeasure: a radius
 * must reach both points, or be that of the half circle as it is shown, rounded to hundredths.
 */
export function canHaveArcMeasure(
  start: Vector3,
  end: Vector3,
  distance: number,
  measure: CurveMeasure,
): boolean {
  const halfChord = Math.hypot(end.x - start.x, end.y - start.y) / 2;
  return (
    measure !== 'radius' ||
    distance >= halfChord ||
    (distance > 0 && distance.toFixed(2) === halfChord.toFixed(2))
  );
}

/**
 * Gets the bulge of the arc with the radius between two points the chord length apart, the longer
 * of the two arcs if `long`. A radius too short to reach both points is a half circle.
 */
function getBulgeFromRadius(chordLength: number, radius: number, long: boolean): number {
  const halfShortSweep = Math.asin(Math.min(chordLength / (2 * Math.abs(radius)), 1));
  const sweep = long ? 2 * Math.PI - 2 * halfShortSweep : 2 * halfShortSweep;
  // The bulge is the tangent of a quarter of the angle the arc sweeps
  return Math.tan(sweep / 4);
}

/**
 * Gets the distance from the line between start and end to the middle of the arc with the bulge.
 */
export function getArcDistance(start: Vector3, end: Vector3, bulge: number): number {
  return (Math.abs(bulge) * Math.hypot(end.x - start.x, end.y - start.y)) / 2;
}

/**
 * Gets the radius of the arc from start to end with the bulge, infinite if it is straight.
 */
export function getArcRadius(start: Vector3, end: Vector3, bulge: number): number {
  const chordLength = Math.hypot(end.x - start.x, end.y - start.y);
  return bulge === 0 ? Infinity : (chordLength * (1 + bulge * bulge)) / (4 * Math.abs(bulge));
}

/**
 * Gets the radius or the bulge of the arc from start to end with the bulge, see CurveMeasure.
 */
export function getArcMeasure(
  start: Vector3,
  end: Vector3,
  bulge: number,
  measure: CurveMeasure,
): number {
  return measure === 'radius' ? getArcRadius(start, end, bulge) : getArcDistance(start, end, bulge);
}

/**
 * The radius or bulge of a curve being bent, see CurveMeasure, or the radius of a circle being
 * created, which is shown in the tooltip, where it can be typed.
 */
export type CurveDistanceInput = {
  /** What the distance is, a bulge by default. */
  kind?: CurveMeasure;
  /** What is created, a curve by default. */
  shape?: 'curve' | 'circle';
  /** The distance (meters) from the cursor, or the typed distance, infinite for a straight curve. */
  getDistance: () => number;
  /** Sets the distance, or undefined to follow the cursor again. */
  setDistance: (distance: number | undefined) => void;
  /** Whether the typed distance can be applied, any by default. */
  isValid?: (distance: number) => boolean;
  /** Applies the curve. */
  apply: () => void;
  cancel: () => void;
};

/**
 * Gets the point that a curve should pass through, from the cursor.
 *
 * Only the position in the horizontal plane is used for the curve. The point is placed where the
 * cursor is at the given height, so that the curve is shown passing through the cursor, unless the
 * cursor is snapped to a point of the design.
 */
export function getCurveThroughPoint(tool: DesignTool, height: number): Vector3 | undefined {
  if (tool.intersection?.object instanceof Points && isDefined(tool.currentPoint)) {
    return tool.currentPoint.clone();
  }

  const camera = cameraManager().getActiveCamera();
  const { position } = tool.lastMouseEvent.pointer;
  const ray = camera?.getRay(position.x, position.y).ray;

  return (ray && findPointOnLineFromHeight(ray.origin, ray.direction, height)) ?? tool.currentPoint;
}

/**
 * Replaces a curved line segment with points along the curve, connected by straight line segments.
 * The new points and line segments get the attributes of the curve, and the line segments its width
 * and stripes, which continue along them.
 * Returns the changes and the new line segments, in order from the start to the end of the curve.
 */
export function densifyCurve(
  node: DesignNode,
  id: LineSegmentId,
): { changes: RevertChange[]; lineSegmentIds: LineSegmentId[] } {
  const curve = node.getLineSegmentById(id);
  const conic = getLineSegmentConic(node, id, 'local')!;
  const count = getConicSegmentCount(conic, densifyTolerance, maxDensifySegments);

  const changes: RevertChange[] = [node.removeLineSegment(id)];
  const pointIds: PointId[] = [curve.values[0]];

  for (let i = 1; i < count; i++) {
    const change = node.addPoint(getConicPoint(conic, i / count), curve.attributes);
    changes.unshift(change);
    pointIds.push(change.id);
  }

  pointIds.push(curve.values[1]);

  const lineSegmentIds: LineSegmentId[] = [];

  for (let i = 1; i < pointIds.length; i++) {
    const distance = getConicLength(conic, 0, (i - 1) / count);
    const change = node.addLineSegment(pointIds[i - 1], pointIds[i], curve.attributes, undefined, {
      width: curve.width,
      stripe: curve.stripe && { ...curve.stripe, offset: curve.stripe.offset + distance },
    });
    changes.unshift(change);
    lineSegmentIds.push(change.id);
  }

  return { changes, lineSegmentIds };
}

/**
 * Divides a line, curved if it has a curve, into pieces of the length along it, and the rest.
 * Returns the points between the pieces, the curves of the pieces, and how far along the line each
 * piece starts.
 */
export function divideLine(
  start: Vector3,
  end: Vector3,
  curve: SegmentCurve | undefined,
  length: number,
): { points: Vector3[]; curves: (SegmentCurve | undefined)[]; distances: number[] } {
  const conic = curve && getSegmentConic(start, end, curve);
  const total = conic ? getConicLength(conic) : start.distanceTo(end);
  const distances = [0];

  // Pieces shorter than this at the end are left out, the last piece is a bit longer instead.
  const precision = 1e-6;

  while (distances[distances.length - 1] + length < total - precision) {
    distances.push(distances[distances.length - 1] + length);
  }

  if (!conic) {
    return {
      points: distances.slice(1).map((d) => start.clone().lerp(end, d / total)),
      curves: distances.map(() => undefined),
      distances,
    };
  }

  const ts = [...getConicParametersAtLengths(conic, distances.slice(1)), 1];
  const points = ts.slice(0, -1).map((t) => getConicPoint(conic, t));
  const curves = ts.map((t, i) => getSegmentCurve(getConicPart(conic, i === 0 ? 0 : ts[i - 1], t)));

  return { points, curves, distances };
}
